import { useState, useEffect } from 'react'
import {
  Assessment,
  Evidence,
  AuditEvent,
  RiskLevel,
  SystemDefinition,
  RegulationType,
  RemediationAction,
  RemediationStatus,
} from '../types/assessment'

const STORAGE_KEY = 'provenance_one_assessment'

const INITIAL_ASSESSMENT: Assessment = {
  id: crypto.randomUUID(),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  regulation: null,
  systemDefinition: {
    name: '',
    description: '',
    useCase: '',
    impact: '',
  },
  riskLevel: null,
  evidence: [],
  status: 'in-progress',
  auditTrail: [],
  remediationActions: [],
}

export function useAssessment() {
  const [assessment, setAssessment] = useState<Assessment>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Ensure remediationActions exists even if it's missing from old data
      return {
        ...parsed,
        remediationActions: parsed.remediationActions || [],
      }
    }
    return INITIAL_ASSESSMENT
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assessment))
  }, [assessment])

  const addAuditEvent = (action: string, details?: string) => {
    const event: AuditEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user: 'Current User',
      action,
      details,
    }

    const updatedAssessment = {
      ...assessment,
      updatedAt: new Date().toISOString(),
      auditTrail: [event, ...assessment.auditTrail],
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedAssessment))
    setAssessment(updatedAssessment)
  }

  const updateSystemDefinition = (def: SystemDefinition) => {
    const event: AuditEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user: 'Current User',
      action: 'System Definition Updated',
      details: `Updated definition for ${def.name}`,
    }

    const updatedAssessment = {
      ...assessment,
      systemDefinition: def,
      updatedAt: new Date().toISOString(),
      auditTrail: [event, ...assessment.auditTrail],
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedAssessment))
    setAssessment(updatedAssessment)
  }

  const setRegulation = (reg: RegulationType) => {
    const event: AuditEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user: 'Current User',
      action: 'Regulation Selected',
      details: `Selected ${reg}`,
    }

    const updatedAssessment = {
      ...assessment,
      regulation: reg,
      updatedAt: new Date().toISOString(),
      auditTrail: [event, ...assessment.auditTrail],
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedAssessment))
    setAssessment(updatedAssessment)
  }

  const setRiskLevel = (level: RiskLevel) => {
    const event: AuditEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user: 'Current User',
      action: 'Risk Classification Completed',
      details: `Classified as ${level} Risk`,
    }

    const updatedAssessment = {
      ...assessment,
      riskLevel: level,
      updatedAt: new Date().toISOString(),
      auditTrail: [event, ...assessment.auditTrail],
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedAssessment))
    setAssessment(updatedAssessment)
  }

  const addEvidence = (evidence: Evidence) => {
    const event: AuditEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user: 'Current User',
      action: 'Evidence Uploaded',
      details: `Uploaded ${evidence.fileName} (${evidence.type})`,
    }

    const updatedAssessment = {
      ...assessment,
      evidence: [...assessment.evidence, evidence],
      updatedAt: new Date().toISOString(),
      auditTrail: [event, ...assessment.auditTrail],
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedAssessment))
    setAssessment(updatedAssessment)
  }

  const addRemediationAction = (action: RemediationAction) => {
    const event: AuditEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user: 'Current User',
      action: 'Remediation Action Created',
      details: `Created action for ${action.obligationId}: ${action.description}`,
    }

    const currentActions = assessment.remediationActions || []

    const updatedAssessment = {
      ...assessment,
      remediationActions: [...currentActions, action],
      updatedAt: new Date().toISOString(),
      auditTrail: [event, ...assessment.auditTrail],
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedAssessment))
    setAssessment(updatedAssessment)
  }

  const updateRemediationAction = (
    actionId: string,
    status: RemediationStatus,
  ) => {
    const currentActions = assessment.remediationActions || []
    const updatedActions = currentActions.map((action) =>
      action.id === actionId
        ? {
            ...action,
            status,
            completedAt:
              status === 'completed'
                ? new Date().toISOString()
                : action.completedAt,
          }
        : action,
    )

    const event: AuditEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user: 'Current User',
      action: 'Remediation Action Updated',
      details: `Updated action status to ${status}`,
    }

    const updatedAssessment = {
      ...assessment,
      remediationActions: updatedActions,
      updatedAt: new Date().toISOString(),
      auditTrail: [event, ...assessment.auditTrail],
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedAssessment))
    setAssessment(updatedAssessment)
  }

  const loadAssessment = (assessmentToLoad: Assessment) => {
    // Ensure remediationActions exists
    const assessmentWithActions = {
      ...assessmentToLoad,
      remediationActions: assessmentToLoad.remediationActions || [],
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(assessmentWithActions))
    setAssessment(assessmentWithActions)
  }

  const getCurrentAssessment = (): Assessment => {
    return assessment
  }

  const resetAssessment = () => {
    const newAssessment: Assessment = {
      ...INITIAL_ASSESSMENT,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      remediationActions: [],
      auditTrail: [
        {
          id: crypto.randomUUID(),
          timestamp: new Date().toISOString(),
          user: 'Current User',
          action: 'Assessment Started',
          details: 'New assessment initialized',
        },
      ],
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newAssessment))
    setAssessment(newAssessment)
  }

  return {
    assessment,
    updateSystemDefinition,
    setRegulation,
    setRiskLevel,
    addEvidence,
    addRemediationAction,
    updateRemediationAction,
    loadAssessment,
    getCurrentAssessment,
    resetAssessment,
  }
}
